import { Controller } from "@hotwired/stimulus"

// Stimulus controller for drag-and-drop file upload zone.
//
// Usage in views:
//   <div data-controller="upload-zone"
//        data-upload-zone-url-value="/admin/attachments"
//        data-upload-zone-attachable-type-value="Client"
//        data-upload-zone-attachable-id-value="42">
//     <div data-upload-zone-target="dropArea">
//       <input type="file" multiple data-upload-zone-target="fileInput"
//              data-action="change->upload-zone#handleFiles">
//     </div>
//     <select data-upload-zone-target="categorySelect">...</select>
//     <div data-upload-zone-target="progressArea">...</div>
//     <div data-upload-zone-target="progressBar"></div>
//     <div data-upload-zone-target="progressText"></div>
//   </div>
//
export default class extends Controller {
  static values = {
    url: String,
    attachableType: String,
    attachableId: String
  }

  static targets = [
    "dropArea",
    "fileInput",
    "categorySelect",
    "progressArea",
    "progressBar",
    "progressText"
  ]

  connect() {
    this.bindDragEvents()
  }

  disconnect() {
    this.unbindDragEvents()
  }

  bindDragEvents() {
    const area = this.dropAreaTarget

    this._onDragOver = (e) => {
      e.preventDefault()
      e.stopPropagation()
      area.classList.add('drag-over')
    }

    this._onDragLeave = (e) => {
      e.preventDefault()
      e.stopPropagation()
      area.classList.remove('drag-over')
    }

    this._onDrop = (e) => {
      e.preventDefault()
      e.stopPropagation()
      area.classList.remove('drag-over')
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.uploadFiles(files)
      }
    }

    this._onClick = (e) => {
      // Only trigger file input when clicking the drop area itself, not child buttons
      if (e.target.closest('a, button, [data-turbo-method]')) return
      this.fileInputTarget.click()
    }

    area.addEventListener('dragover', this._onDragOver)
    area.addEventListener('dragleave', this._onDragLeave)
    area.addEventListener('drop', this._onDrop)
    area.addEventListener('click', this._onClick)
  }

  unbindDragEvents() {
    const area = this.dropAreaTarget
    if (this._onDragOver) area.removeEventListener('dragover', this._onDragOver)
    if (this._onDragLeave) area.removeEventListener('dragleave', this._onDragLeave)
    if (this._onDrop) area.removeEventListener('drop', this._onDrop)
    if (this._onClick) area.removeEventListener('click', this._onClick)
  }

  handleFiles(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  async uploadFiles(files) {
    const total = files.length
    let completed = 0

    this.showProgress()
    this.updateProgress(0, `Uploading 0/${total} files...`)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')

    for (const file of files) {
      try {
        const formData = new FormData()
        formData.append('attachment[file]', file)
        formData.append('attachment[category]', this.categorySelectTarget.value)
        formData.append('attachment[title]', file.name)
        formData.append('attachable_type', this.attachableTypeValue)
        formData.append('attachable_id', this.attachableIdValue)

        const response = await fetch(this.urlValue, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken ? csrfToken.content : '',
            'Accept': 'text/vnd.turbo-stream.html, text/html'
          },
          body: formData
        })

        if (response.ok) {
          const responseText = await response.text()
          // Render Turbo Stream response by appending to the document.
          // Turbo automatically processes <turbo-stream> elements when
          // they are inserted into the DOM.
          Turbo.renderStreamMessage(responseText)
        } else {
          console.error(`Upload failed for ${file.name}: ${response.statusText}`)
        }
      } catch (error) {
        console.error(`Upload error for ${file.name}:`, error)
      }

      completed++
      const percent = Math.round((completed / total) * 100)
      this.updateProgress(percent, `Uploading ${completed}/${total} files...`)
    }

    this.updateProgress(100, 'Upload complete!')

    // Reset file input
    this.fileInputTarget.value = ''

    // Hide progress after a short delay
    setTimeout(() => {
      this.hideProgress()
    }, 1500)
  }

  showProgress() {
    this.progressAreaTarget.classList.remove('d-none')
  }

  hideProgress() {
    this.progressAreaTarget.classList.add('d-none')
  }

  updateProgress(percent, text) {
    this.progressBarTarget.style.width = `${percent}%`
    this.progressTextTarget.textContent = text
  }
}
